import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app, db } from "../../firebase";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getMintEngineUrl } from "../../config";

const CreateArtist = () => {
  const navigate = useNavigate();
  const storage = getStorage(app);

  const [formData, setFormData] = useState({
    // Informations de base
    firstName: "",
    lastName: "",
    walletAddress: "",
    artistShare: "",
    status: "active",
    statusPro: "",
    slogan: "",
    exhibitionId: "",

    // Images (fichiers)
    coverPictureFile: null,
    descriptionCoverPictureFile: null,
    profilePictureFile: null,
    imageVideoPresentationFile: null,
    videoPresentationFile: null,

    // URLs des images (après upload)
    coverPicture: "",
    descriptionCoverPicture: "",
    profilePicture: "",
    imageVideoPresentation: "",
    videoPresentation: "",

    // Links
    facebook: "",
    instagram: "",
    twitter: "",
    website: "",
    linkedin: "",

    // Sections modulables (minimum 1)
    sections: [
      {
        id: Date.now(),
        type: "description", // "description", "leftRight", ou "events"
        title: "",
        description: "",
        imgFile: null,
        img: "",
        leftTitle: "",
        leftText: "",
        rightTitle: "",
        rightText: "",
        events: [{ text: "", year: "" }],
      },
    ],
  });

  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextSectionId, setNextSectionId] = useState(2);

  // Générer automatiquement l'artistId
  const artistId = formData.firstName && formData.lastName
    ? `${formData.firstName}-${formData.lastName}`
    : "";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!artistId) {
      alert("Veuillez d'abord saisir le First Name et Last Name avant d'uploader des fichiers");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    // Upload immédiat vers Firebase Storage
    setIsUploading(true);
    try {
      const storageRef = ref(
        storage,
        `static-artists/${artistId}/${file.name}`
      );
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setFormData((prev) => ({
        ...prev,
        [fieldName.replace("File", "")]: downloadURL,
      }));
    } catch (error) {
      console.error(`Erreur lors de l'upload de ${fieldName}:`, error);
      alert(`Erreur lors de l'upload: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSectionFileChange = async (e, sectionId, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!artistId) {
      alert("Veuillez d'abord saisir le First Name et Last Name avant d'uploader des fichiers");
      return;
    }

    setIsUploading(true);
    try {
      const storageRef = ref(
        storage,
        `static-artists/${artistId}/${file.name}`
      );
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId
            ? { ...section, [fieldName.replace("File", "")]: downloadURL, [fieldName]: file }
            : section
        ),
      }));
    } catch (error) {
      console.error(`Erreur lors de l'upload:`, error);
      // Ne pas bloquer l'utilisateur en cas d'erreur d'upload
      alert(`Erreur lors de l'upload: ${error.message}. Vous pourrez ajouter l'URL manuellement.`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSectionChange = (sectionId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId ? { ...section, [field]: value } : section
      ),
    }));
  };

  const handleEventChange = (sectionId, eventIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id === sectionId) {
          const newEvents = [...section.events];
          newEvents[eventIndex] = { ...newEvents[eventIndex], [field]: value };
          return { ...section, events: newEvents };
        }
        return section;
      }),
    }));
  };

  const addEvent = (sectionId) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId
          ? { ...section, events: [...section.events, { text: "", year: "" }] }
          : section
      ),
    }));
  };

  const removeEvent = (sectionId, eventIndex) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            events: section.events.filter((_, i) => i !== eventIndex),
          };
        }
        return section;
      }),
    }));
  };

  const addSection = () => {
    setFormData((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          id: nextSectionId,
          type: "description",
          title: "",
          description: "",
          imgFile: null,
          img: "",
          leftTitle: "",
          leftText: "",
          rightTitle: "",
          rightText: "",
          events: [{ text: "", year: "" }],
        },
      ],
    }));
    setNextSectionId((prev) => prev + 1);
  };

  const removeSection = (sectionId) => {
    if (formData.sections.length <= 1) {
      alert("Vous devez avoir au moins une section");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.id !== sectionId),
    }));
  };

  const handleSubmit = async () => {
    // Validation des champs requis
    if (!formData.firstName || !formData.lastName) {
      alert("Veuillez remplir le First Name et Last Name");
      return;
    }

    if (!formData.walletAddress) {
      alert("Veuillez remplir le Wallet Address");
      return;
    }

    if (!formData.artistShare || formData.artistShare < 1 || formData.artistShare > 100) {
      alert("Veuillez remplir l'Artist Share (entre 1 et 100)");
      return;
    }

    setIsSubmitting(true);

    try {
      // Étape 1: Créer le payment splitter
      const apiUrl = getMintEngineUrl();
      const paymentSplitterResponse = await fetch(`${apiUrl}/create-payment-splitter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          artistAddress: formData.walletAddress,
          artistShare: formData.artistShare.toString(),
        }),
      });

      if (!paymentSplitterResponse.ok) {
        const errorData = await paymentSplitterResponse.json();
        throw new Error(errorData.error || `Erreur HTTP: ${paymentSplitterResponse.status}`);
      }

      const paymentSplitterData = await paymentSplitterResponse.json();
      const paymentSplitterAddress = paymentSplitterData.contractAddress;

      // Étape 2: Formater les données pour Firebase
      const links = {};
      if (formData.facebook) links.facebook = formData.facebook;
      if (formData.instagram) links.instagram = formData.instagram;
      if (formData.twitter) links.twitter = formData.twitter;
      if (formData.website) links.website = formData.website;
      if (formData.linkedin) links.linkedin = formData.linkedin;

      // Formater les sections selon leur type
      const moreAbout = {};
      formData.sections.forEach((section, index) => {
        const sectionKey = `section${index + 1}`;

        if (section.type === "description") {
          moreAbout[sectionKey] = {
            title: section.title,
            desctiption: section.description, // Note: typo dans Firebase
            img: section.img || "",
          };
        } else if (section.type === "leftRight") {
          moreAbout[sectionKey] = {
            leftTitle: section.leftTitle,
            leftText: section.leftText,
            rightTitle: section.rightTitle,
            rightText: section.rightText,
          };
        } else if (section.type === "events") {
          // Filtrer les événements vides
          const validEvents = section.events.filter(
            (event) => event.text && event.year
          );

          moreAbout[sectionKey] = {
            title: section.title,
            img: section.img || "",
            events: validEvents.length > 0 ? validEvents : [],
          };
        }
      });

      // Créer l'objet artiste pour Firebase
      const artistData = {
        artistId: artistId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        walletAddress: formData.walletAddress,
        paymentSplitterAddress: paymentSplitterAddress,
        status: formData.status,
        statusPro: formData.statusPro || "",
        slogan: formData.slogan || "",
        exhibitionId: formData.exhibitionId || "",
        coverPicture: formData.coverPicture,
        descriptionCoverPicture: formData.descriptionCoverPicture || "",
        profilePicture: formData.profilePicture,
        imageVideoPresentation: formData.imageVideoPresentation || "",
        videoPresentation: formData.videoPresentation || "",
        links: links,
        moreAbout: moreAbout,
        creationTime: serverTimestamp(),
      };

      // Étape 3: Créer l'artiste dans Firestore
      const artistRef = doc(db, "artists", artistId);
      await setDoc(artistRef, artistData);

      console.log("Artiste créé:", artistData);

      // Rediriger vers la page de détails de l'artiste
      navigate(`/artist/${artistId}`);
    } catch (error) {
      console.error("Erreur lors de la création de l'artiste:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold text-white mb-6">Créer un Artiste</h1>

        {/* Informations de base */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Informations de base</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist ID (généré automatiquement)
              </label>
              <input
                type="text"
                value={artistId}
                disabled
                className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed"
                placeholder="Sera généré à partir de First Name - Last Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Wallet Address *
              </label>
              <input
                type="text"
                name="walletAddress"
                value={formData.walletAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="0x..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist Share (%) *
              </label>
              <input
                type="number"
                name="artistShare"
                value={formData.artistShare}
                onChange={handleInputChange}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="1-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Status Pro
              </label>
              <input
                type="text"
                name="statusPro"
                value={formData.statusPro}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="active" className="bg-gray-700">Active</option>
                <option value="upcoming" className="bg-gray-700">Upcoming</option>
                <option value="inactive" className="bg-gray-700">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Exhibition ID
              </label>
              <input
                type="text"
                name="exhibitionId"
                value={formData.exhibitionId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-1">
                Slogan
              </label>
              <textarea
                name="slogan"
                value={formData.slogan}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Cover Picture
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "coverPictureFile")}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {formData.coverPicture && (
                <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Description Cover Picture
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "descriptionCoverPictureFile")}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {formData.descriptionCoverPicture && (
                <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Profile Picture
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "profilePictureFile")}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {formData.profilePicture && (
                <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Image Video Presentation
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "imageVideoPresentationFile")}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {formData.imageVideoPresentation && (
                <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Video Presentation
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileChange(e, "videoPresentationFile")}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {formData.videoPresentation && (
                <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
              )}
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Liens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Facebook
              </label>
              <input
                type="url"
                name="facebook"
                value={formData.facebook}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Instagram
              </label>
              <input
                type="url"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Twitter
              </label>
              <input
                type="url"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                LinkedIn
              </label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
          </div>
        </div>

        {/* Sections modulables */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white">Sections (More About)</h2>
            <button
              type="button"
              onClick={addSection}
              className="px-4 py-2 bg-[#33c7b6] text-[#00263a] rounded-md hover:bg-[#2ab3a3] font-semibold"
            >
              Ajouter une section
            </button>
          </div>

          {formData.sections.map((section, sectionIndex) => (
            <div key={section.id} className="mb-6 p-4 bg-gray-700 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Section {sectionIndex + 1}</h3>
                <div className="flex gap-2">
                  <select
                    value={section.type}
                    onChange={(e) => handleSectionChange(section.id, "type", e.target.value)}
                    className="px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="description" className="bg-gray-700">Description</option>
                    <option value="leftRight" className="bg-gray-700">Left/Right Text</option>
                    <option value="events" className="bg-gray-700">Events</option>
                  </select>
                  {formData.sections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSection(section.id)}
                      className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              </div>

              {/* Champs communs à tous les types */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-white mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => handleSectionChange(section.id, "title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                />
              </div>

              {/* Type: Description */}
              {section.type === "description" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-white mb-1">
                      Description
                    </label>
                    <textarea
                      value={section.description}
                      onChange={(e) => handleSectionChange(section.id, "description", e.target.value)}
                      rows="5"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-white mb-2">
                      Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSectionFileChange(e, section.id, "imgFile")}
                      className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
                    />
                    {section.img && (
                      <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
                    )}
                  </div>
                </>
              )}

              {/* Type: Left/Right */}
              {section.type === "leftRight" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">
                      Left Title
                    </label>
                    <input
                      type="text"
                      value={section.leftTitle}
                      onChange={(e) => handleSectionChange(section.id, "leftTitle", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">
                      Right Title
                    </label>
                    <input
                      type="text"
                      value={section.rightTitle}
                      onChange={(e) => handleSectionChange(section.id, "rightTitle", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">
                      Left Text
                    </label>
                    <textarea
                      value={section.leftText}
                      onChange={(e) => handleSectionChange(section.id, "leftText", e.target.value)}
                      rows="5"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">
                      Right Text
                    </label>
                    <textarea
                      value={section.rightText}
                      onChange={(e) => handleSectionChange(section.id, "rightText", e.target.value)}
                      rows="5"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                    />
                  </div>
                </div>
              )}

              {/* Type: Events */}
              {section.type === "events" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-white mb-2">
                      Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSectionFileChange(e, section.id, "imgFile")}
                      className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
                    />
                    {section.img && (
                      <p className="mt-2 text-xs text-green-400">✓ Uploadé</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Events
                    </label>
                    {section.events.map((event, eventIndex) => (
                      <div key={eventIndex} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="Text"
                          value={event.text}
                          onChange={(e) => handleEventChange(section.id, eventIndex, "text", e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                        />
                        <input
                          type="text"
                          placeholder="Year"
                          value={event.year}
                          onChange={(e) => handleEventChange(section.id, eventIndex, "year", e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-600 bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                        />
                        {section.events.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEvent(section.id, eventIndex)}
                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addEvent(section.id)}
                      className="mt-2 px-4 py-2 bg-[#33c7b6] text-[#00263a] rounded-md hover:bg-[#2ab3a3] font-semibold"
                    >
                      Ajouter un événement
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Bouton Confirmer */}
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`${isSubmitting
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-[#33c7b6] hover:bg-[#2ab3a3]"
              } text-[#00263a] px-8 py-3 rounded-full font-semibold transition-colors`}
          >
            {isSubmitting ? "Création en cours..." : "Confirmer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateArtist;
